import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import {
  ResizeNotifier,
  yieldToMainThread,
  getViewParameterValue,
} from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} [fieldsets]
 * @property {HTMLElement} [overflowList]
 */

/**
 * Variant picker custom element.
 *
 * Shopify's rendered response is always the source of truth for the selected
 * variant, availability, price, inventory, checkout buttons, and media.
 *
 * @extends Component<VariantPickerRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number} */
  #requestSequence = 0;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {ResizeNotifier} */
  #resizeObserver = new ResizeNotifier(() => {
    this.updateVariantPickerCss();
  });

  /** @type {EventListener} */
  #handleChange = (event) => {
    this.variantChanged(event);
  };

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.#handleChange);
    this.#resizeObserver.observe(this);

    this.#syncVariantPickerState();
    this.updateVariantPickerCss();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.#resizeObserver.disconnect();
    this.#abortController?.abort();

    super.disconnectedCallback();
  }

  /**
   * Rebuild cached radio state from the current DOM.
   * This must run after Shopify morphs the variant picker.
   */
  #syncVariantPickerState() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      Array.from(this.querySelectorAll('fieldset'))
    );

    this.#checkedIndices = [];
    this.#radios = [];

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const radios = /** @type {HTMLInputElement[]} */ (
        Array.from(
          fieldset.querySelectorAll('input[type="radio"]')
        )
      );

      this.#radios[fieldsetIndex] = radios;

      const checkedIndex = radios.findIndex(
        (radio) => radio.checked
      );

      this.#checkedIndices[fieldsetIndex] =
        checkedIndex >= 0 ? [checkedIndex] : [];
    });
  }

  /**
   * @param {Event} event
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement
        ? event.target.options[event.target.selectedIndex]
        : event.target;

    if (
      !selectedOption ||
      !(
        selectedOption instanceof HTMLInputElement ||
        selectedOption instanceof HTMLOptionElement
      )
    ) {
      return;
    }

    this.updateSelectedOption(event.target);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentProductUrl =
      this.dataset.productUrl?.split('?')[0];

    const connectedProductUrl =
      selectedOption.dataset.connectedProductUrl || '';

    const loadsNewProduct =
      isOnProductPage &&
      Boolean(connectedProductUrl) &&
      connectedProductUrl.split('?')[0] !== currentProductUrl;

    const isOnFeaturedProductSection = Boolean(
      this.closest('featured-product-information')
    );

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
        ? 'featured-product-information'
        : undefined;

    const {
      optionValueId = '',
      variantId = '',
    } = selectedOption.dataset;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      detail: {
        optionValueId,
        variantId,
        connectedProductUrl,
      },
      history: {
        isOnProductPage,
        loadsNewProduct,
      },
    });
  }

  /**
   * @param {number} fieldsetIndex
   * @returns {{
   *   fieldset: HTMLFieldSetElement,
   *   currentIndex: number | undefined,
   *   previousIndex: number | undefined,
   *   currentWidth: number | undefined,
   *   previousWidth: number | undefined
   * } | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      Array.from(this.querySelectorAll('fieldset'))
    );

    const fieldset = fieldsets[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];

    if (!fieldset || !radios || !checkedIndices) {
      return null;
    }

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth:
        currentIndex !== undefined
          ? radios[currentIndex]?.parentElement?.offsetWidth
          : undefined,
      previousWidth:
        previousIndex !== undefined
          ? radios[previousIndex]?.parentElement?.offsetWidth
          : undefined,
    };
  }

  /**
   * @param {{
   *   fieldset: HTMLFieldSetElement,
   *   currentIndex: number | undefined,
   *   previousIndex: number | undefined,
   *   currentWidth: number | undefined,
   *   previousWidth: number | undefined
   * }} measurements
   */
  #applyFieldsetMeasurements({
    fieldset,
    currentIndex,
    previousIndex,
    currentWidth,
    previousWidth,
  }) {
    if (currentWidth) {
      fieldset.style.setProperty(
        '--pill-width-current',
        `${currentWidth}px`
      );
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty(
        '--pill-width-previous',
        `${previousWidth}px`
      );
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * @param {number} fieldsetIndex
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements =
      this.#getFieldsetMeasurements(fieldsetIndex);

    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * @param {HTMLElement | string} target
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(
        `[data-option-value-id="${CSS.escape(target)}"]`
      );

      if (!(targetElement instanceof HTMLElement)) {
        throw new Error(
          '[variant-picker] Target element not found'
        );
      }

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldset = target.closest('fieldset');

      if (!fieldset) {
        target.checked = true;
        return;
      }

      const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
        Array.from(this.querySelectorAll('fieldset'))
      );

      const fieldsetIndex = fieldsets.indexOf(fieldset);
      const radios = /** @type {HTMLInputElement[]} */ (
        Array.from(
          fieldset.querySelectorAll('input[type="radio"]')
        )
      );
      const inputIndex = radios.indexOf(target);

      target.checked = true;

      if (fieldsetIndex === -1 || inputIndex === -1) {
        this.#syncVariantPickerState();
        this.updateVariantPickerCss();
        return;
      }

      this.#radios[fieldsetIndex] = radios;

      const checkedIndices =
        this.#checkedIndices[fieldsetIndex] || [];

      const previousCurrentIndex = checkedIndices[0];

      if (
        previousCurrentIndex !== undefined &&
        radios[previousCurrentIndex]
      ) {
        radios[previousCurrentIndex].dataset.currentChecked =
          'false';
        radios[previousCurrentIndex].dataset.previousChecked =
          'true';
      }

      if (checkedIndices[0] !== inputIndex) {
        checkedIndices.unshift(inputIndex);
      }

      checkedIndices.length = Math.min(
        checkedIndices.length,
        2
      );

      this.#checkedIndices[fieldsetIndex] = checkedIndices;

      radios.forEach((radio, index) => {
        if (index === checkedIndices[0]) {
          radio.dataset.currentChecked = 'true';
          radio.dataset.previousChecked = 'false';
        } else if (index === checkedIndices[1]) {
          radio.dataset.currentChecked = 'false';
          radio.dataset.previousChecked = 'true';
        } else {
          radio.dataset.currentChecked = 'false';
          radio.dataset.previousChecked = 'false';
        }
      });

      this.updateFieldsetCss(fieldsetIndex);
      return;
    }

    if (target instanceof HTMLSelectElement) {
      const selected = target.options[target.selectedIndex];

      if (!selected) {
        throw new Error(
          '[variant-picker] Selected option not found'
        );
      }

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      selected.setAttribute('selected', 'selected');
    }
  }

  /**
   * @returns {string[]}
   */
  #getCurrentOptionValueIds() {
    const optionValueIds = [];

    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];

      if (selected?.dataset.optionValueId) {
        optionValueIds.push(selected.dataset.optionValueId);
      }
    }

    for (
      const input of this.querySelectorAll(
        'fieldset input[type="radio"]:checked'
      )
    ) {
      if (input.dataset.optionValueId) {
        optionValueIds.push(input.dataset.optionValueId);
      }
    }

    return optionValueIds;
  }

  /**
   * @param {HTMLElement} selectedOption
   * @param {string | null} source
   * @param {string[]} sourceSelectedOptionsValues
   * @returns {string}
   */
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = []
  ) {
    const productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;

    if (!productUrl) {
      throw new Error(
        '[variant-picker] Missing product URL'
      );
    }

    this.#pendingRequestUrl = productUrl;

    const requestUrl = new URL(
      productUrl,
      window.location.origin
    );

    const viewParamValue = getViewParameterValue();

    if (viewParamValue) {
      requestUrl.searchParams.set('view', viewParamValue);
    }

    let optionValueIds;

    if (source === 'product-card') {
      optionValueIds = sourceSelectedOptionsValues.length
        ? sourceSelectedOptionsValues
        : [selectedOption.dataset.optionValueId || ''];
    } else {
      optionValueIds = this.#getCurrentOptionValueIds();
    }

    optionValueIds = optionValueIds.filter(Boolean);

    if (optionValueIds.length) {
      requestUrl.searchParams.set(
        'option_values',
        optionValueIds.join(',')
      );
    } else {
      requestUrl.searchParams.delete('option_values');
    }

    const sectionIdMap = {
      'quick-add-component':
        'section-rendering-product-card',
      'swatches-variant-picker-component':
        'section-rendering-product-card',
      'featured-product-information':
        this.closest('featured-product-information')?.id,
    };

    const closestSectionSelector = Object.keys(
      sectionIdMap
    ).find((selector) => this.closest(selector));

    if (closestSectionSelector) {
      const sectionId =
        sectionIdMap[closestSectionSelector];

      if (sectionId) {
        requestUrl.searchParams.set('section_id', sectionId);
      }
    }

    return requestUrl.toString();
  }

  /**
   * @param {{
   *   isOnProductPage?: boolean,
   *   loadsNewProduct?: boolean
   * }} history
   * @param {string} resolvedVariantId
   * @param {string} connectedProductUrl
   * @param {HTMLElement | null} currentPicker
   * @param {number} requestSequence
   */
  async #updateBrowserUrl(
    history,
    resolvedVariantId,
    connectedProductUrl,
    currentPicker,
    requestSequence
  ) {
    if (!history.isOnProductPage) return;

    let nextUrl = new URL(window.location.href);

    if (history.loadsNewProduct && connectedProductUrl) {
      const serverProductUrl =
        currentPicker?.dataset.productUrl ||
        connectedProductUrl;

      nextUrl = new URL(
        serverProductUrl,
        window.location.origin
      );
    }

    if (resolvedVariantId) {
      nextUrl.searchParams.set('variant', resolvedVariantId);
    } else {
      nextUrl.searchParams.delete('variant');
    }

    if (nextUrl.href === window.location.href) return;

    await yieldToMainThread();

    if (requestSequence !== this.#requestSequence) {
      return;
    }

    if (nextUrl.href !== window.location.href) {
      history.replaceState({}, '', nextUrl.toString());
    }
  }

  /**
   * @param {string} requestUrl
   * @param {{
   *   morphElementSelector?: string,
   *   detail?: {
   *     optionValueId?: string,
   *     variantId?: string,
   *     connectedProductUrl?: string
   *   },
   *   history?: {
   *     isOnProductPage?: boolean,
   *     loadsNewProduct?: boolean
   *   }
   * }} options
   */
  async fetchUpdatedSection(
    requestUrl,
    {
      morphElementSelector,
      detail = {},
      history = {},
    } = {}
  ) {
    const {
      optionValueId = '',
      variantId = '',
      connectedProductUrl = '',
    } = detail;

    this.#abortController?.abort();

    const controller = new AbortController();
    this.#abortController = controller;

    const requestSequence = ++this.#requestSequence;

    const deferredEventPromise =
      ProductSelectEvent.createPromise();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions: this.getAllSelectedOptions(),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(requestUrl, {
        signal: controller.signal,
        headers: {
          Accept: 'text/html',
        },
        cache: 'no-store',
      });

      if (!response.ok) {
        throw new Error(
          `[variant-picker] Variant update failed: ${response.status}`
        );
      }

      const textContent = await response.text();

      if (controller.signal.aborted) {
        throw new DOMException(
          'Variant request aborted',
          'AbortError'
        );
      }

      if (requestSequence !== this.#requestSequence) {
        throw new DOMException(
          'Variant request superseded',
          'AbortError'
        );
      }

      const newHtml = new DOMParser().parseFromString(
        textContent,
        'text/html'
      );

      const newPicker =
        newHtml.querySelector('variant-picker') ||
        newHtml.querySelector('variant-radios') ||
        newHtml.querySelector('variant-selects');

      const selectedVariantElement =
        newPicker?.querySelector(
          'input[data-variant-id]:checked'
        ) ||
        newPicker?.querySelector(
          'option[data-variant-id]:checked'
        ) ||
        newPicker?.querySelector(
          'option[data-variant-id][selected]'
        );

      const resolvedVariantId =
        selectedVariantElement?.dataset.variantId ||
        variantId;

      const updatedElement = this.updateElement(
        newHtml,
        morphElementSelector || 'product-info'
      );

      if (requestSequence !== this.#requestSequence) {
        throw new DOMException(
          'Variant request superseded',
          'AbortError'
        );
      }

      const currentPicker =
        updatedElement.matches('variant-picker')
          ? updatedElement
          : updatedElement.querySelector('variant-picker');

      if (currentPicker instanceof VariantPicker) {
        currentPicker.#pendingRequestUrl =
          currentPicker.dataset.productUrl ||
          connectedProductUrl ||
          undefined;
        currentPicker.#syncVariantPickerState();
        currentPicker.updateVariantPickerCss();
      }

      await this.#updateBrowserUrl(
        history,
        resolvedVariantId,
        connectedProductUrl,
        currentPicker,
        requestSequence
      );

      if (requestSequence !== this.#requestSequence) {
        throw new DOMException(
          'Variant request superseded',
          'AbortError'
        );
      }

      /*
       * Resolve only after Shopify HTML has been morphed into the page.
       * media-gallery consumes html from this resolved event payload.
       */
      deferredEventPromise.resolve({
        html: newHtml,
        variant: resolvedVariantId
          ? { id: resolvedVariantId }
          : null,
        optionValueId,
        connectedProductUrl,
      });
    } catch (error) {
      deferredEventPromise.reject(error);

      if (error?.name !== 'AbortError') {
        console.error(
          '[variant-picker] Failed to update variant:',
          error
        );
      }
    }
  }

  /**
   * @param {Document} newHtml
   * @param {string} elementSelector
   * @returns {HTMLElement}
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!(element instanceof HTMLElement) || !(newElement instanceof HTMLElement)) {
      throw new Error(
        `[variant-picker] No new element source found for ${elementSelector}`
      );
    }

    morph(element, newElement, MORPH_OPTIONS);

    return element;
  }

  /**
   * @param {Document} newHtml
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!(main instanceof HTMLElement) || !(newMain instanceof HTMLElement)) {
      throw new Error(
        '[variant-picker] No new main source found'
      );
    }

    morph(main, newMain, MORPH_OPTIONS);
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      Array.from(this.querySelectorAll('fieldset'))
    );

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const measurements =
        this.#getFieldsetMeasurements(fieldsetIndex);

      if (measurements) {
        this.#applyFieldsetMeasurements(measurements);
      }
    });
  }

  /**
   * @returns {HTMLInputElement | HTMLOptionElement | undefined}
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option:checked, fieldset input[type="radio"]:checked'
    );

    if (
      selectedOption instanceof HTMLInputElement ||
      selectedOption instanceof HTMLOptionElement
    ) {
      return selectedOption;
    }

    return undefined;
  }

  /**
   * @returns {{name: string, value: string}[]}
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];

      if (selected?.dataset.optionName) {
        options.push({
          name: selected.dataset.optionName,
          value: selected.value,
        });
      }
    }

    for (
      const input of this.querySelectorAll(
        'fieldset input[type="radio"]:checked'
      )
    ) {
      if (input.dataset.optionName) {
        options.push({
          name: input.dataset.optionName,
          value: input.value,
        });
      }
    }

    return options;
  }

  /**
   * @returns {string | undefined}
   */
  get selectedOptionId() {
    return this.selectedOption?.dataset.optionValueId;
  }

  /**
   * @returns {string[]}
   */
  get selectedOptionsValues() {
    return this.#getCurrentOptionValueIds();
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define(
    'variant-picker',
    VariantPicker
  );
}